import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, EditIcon, MessageSquare } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import WillForm from "@/components/WillForm";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AIChatAssistant from "@/components/AIChatAssistant";
import { createWillWithMedia } from '../services/willService';

interface WillDataFromAI {
  title?: string;
  description: string;
  budget?: string;
  currency?: string;
  category?: string;
  location?: string;
  fulfillmentTime?: string;
  fulfillmentTimeType?: string;
  fulfillmentDate?: string;
  recurrencePattern?: string;
  budgetType?: string;
  media?: any[];
}

// Add localStorage key for active tab persistence
const ACTIVE_TAB_STORAGE_KEY = 'wanttd_active_tab';

const CreateWill = () => {
  // Load the active tab from localStorage
  const loadSavedActiveTab = (): "form" | "ai" => {
    try {
      const savedTab = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
      if (savedTab === 'form' || savedTab === 'ai') {
        return savedTab;
      }
    } catch (error) {
      console.error('Error loading active tab:', error);
    }
    return "form";
  };

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { language } = useLanguage();
  const isRTL = language === 'he';
  const [activeTab, setActiveTab] = useState<"form" | "ai">(loadSavedActiveTab);
  const [aiGeneratedData, setAiGeneratedData] = useState<WillDataFromAI | null>(null);

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      navigate('/signin');
    }
  }, [navigate]);

  const handleWillCreated = (will: any) => {
    toast.success("Want created successfully!");
    navigate("/");
  };

  const handleCancel = () => {
    // Clear AI data if user cancels
    setAiGeneratedData(null);
    navigate("/");
  };

  const handleWillDataFromAI = (data: WillDataFromAI) => {
    // Store AI generated data for the WillForm
    setAiGeneratedData(data);
    
    // Switch to form tab after AI generates data
    setActiveTab("form");
    toast.success("AI has populated your want form. Please review and edit if needed.");
  };

  // Update the handleDirectSubmitFromAI function to clear AI chat history after successful submission
  const handleDirectSubmitFromAI = async (data: WillDataFromAI) => {
    try {
      setIsLoading(true);
      
      // Prepare the will data
      const willData = {
        title: data.title || 'New Want',
        description: data.description,
        budget: data.budgetType === 'FIXED' ? parseFloat(data.budget || '0') : 
               (data.budgetType === 'FREE' ? 0 : null),
        location: data.location || '',
        category: data.category || '',
        currency: data.budgetType === 'FIXED' ? data.currency : null,
        fulfillmentTimeType: data.fulfillmentTimeType || 'SPECIFIC_DATE',
        fulfillmentTime: data.fulfillmentTimeType === 'SPECIFIC_DATE' && data.fulfillmentTime ? 
                        new Date(data.fulfillmentTime).toISOString() : null,
        recurrencePattern: data.recurrencePattern || 'NONE'
      };

      // Get the media files if any
      const mediaFiles = data.media
        ? data.media
            .filter(item => item && item.file instanceof File)
            .map(item => item.file)
        : [];

      // Create the will using the service
      const response = await createWillWithMedia(willData, mediaFiles);
      
      // Clear AI chat localStorage after successful submission
      localStorage.removeItem('wanttd_ai_chat_messages');
      localStorage.removeItem('wanttd_ai_chat_will_data');
      localStorage.removeItem('wanttd_ai_chat_media');
      localStorage.removeItem('wanttd_ai_chat_summary_visible');
      
      // Handle success
      toast.success("Want created successfully!");
      navigate("/");
      
      return response;
    } catch (error) {
      console.error('Error creating want directly:', error);
      toast.error('Failed to create want. Please try again.');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <main className="container mx-auto px-4 pt-24 pb-12">
        <div className="max-w-2xl mx-auto">
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "form" | "ai")}>
            <TabsList className="grid grid-cols-2 mb-8">
              <TabsTrigger value="form" className="flex items-center gap-2">
                <EditIcon className="h-4 w-4" />
                {isRTL ? "טופס רגיל" : "WHAT DO YOU WANT"}
              </TabsTrigger>
              <TabsTrigger value="ai" className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                {isRTL ? "עוזר AI" : "AI ASSISTANT"}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="form">
              <WillForm 
                onSuccess={handleWillCreated}
                onCancel={handleCancel}
                initialData={aiGeneratedData || undefined}
              />
            </TabsContent>

            <TabsContent value="ai" className="min-h-[600px] flex flex-col">
              <AIChatAssistant 
                onWillDataGenerated={handleWillDataFromAI}
                onClose={() => setActiveTab("form")}
                onSubmitWill={handleDirectSubmitFromAI}
              />
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default CreateWill;
